'use client';

import { useRef, useState } from 'react';
import { collectClientData } from '../../browser/client-data';
import { sendEvent } from '../../browser/send-event';
import {
  type ClientDataContextProps,
  type FormPayload,
  type SubmitFormResult,
  buildPayload,
} from '../../core';
import { SUBMISSION_ERROR_MESSAGE } from '../submissionError';

interface UseFormSubmissionProps {
  formId: string;
  tracking: FormPayload['tracking'];
  contextProps?: ClientDataContextProps;
  templateName?: string;
  submitAction: (payload: FormPayload) => Promise<SubmitFormResult>;
}

function createSubmissionId(): string {
  const cryptoObject = globalThis.crypto;

  if (cryptoObject?.randomUUID) {
    return cryptoObject.randomUUID();
  }

  // Fallback for environments without randomUUID (older Safari, embedded
  // contexts). Prefer crypto.getRandomValues over Math.random when available
  // so the IDs are still collision-resistant; degrade to Math.random only on
  // the rare browser without either crypto API.
  if (cryptoObject?.getRandomValues) {
    const bytes = new Uint8Array(16);
    cryptoObject.getRandomValues(bytes);
    const hex = Array.from(bytes, (b) => b.toString(16).padStart(2, '0')).join(
      ''
    );
    return `bx-form-${hex}`;
  }

  return `bx-form-${Date.now()}-${Math.random().toString(36).slice(2)}`;
}

export function useFormSubmission({
  formId,
  tracking,
  contextProps,
  templateName = 'unknown',
  submitAction,
}: UseFormSubmissionProps) {
  const [isLoading, setIsLoading] = useState(false);
  const [submissionErrorMessage, setSubmissionErrorMessage] = useState<
    string | null
  >(null);
  const submissionIdRef = useRef<string | null>(null);
  const firstSubmittedAtRef = useRef<string | null>(null);
  const hasStartedMultiStepRef = useRef(false);

  const getSubmissionMetadata = (formData: Record<string, unknown>) => {
    const isInitialStep = formData.hasSecondPage === true;
    if (isInitialStep) {
      hasStartedMultiStepRef.current = true;
    }

    submissionIdRef.current ??= createSubmissionId();
    firstSubmittedAtRef.current ??= new Date().toISOString();

    return {
      submissionId: submissionIdRef.current,
      firstSubmittedAt: firstSubmittedAtRef.current,
      submissionPhase: isInitialStep
        ? ('initial' as const)
        : hasStartedMultiStepRef.current
          ? ('followUp' as const)
          : ('singleStep' as const),
    };
  };

  const resetSubmissionMetadata = () => {
    submissionIdRef.current = null;
    firstSubmittedAtRef.current = null;
    hasStartedMultiStepRef.current = false;
    setSubmissionErrorMessage(null);
  };

  const submitToApi = async (
    formData: Record<string, unknown>,
    extras: Record<string, unknown> = {}
  ) => {
    setIsLoading(true);
    setSubmissionErrorMessage(null);

    try {
      const clientData = collectClientData(formId, contextProps);

      const payload = buildPayload({
        baseFormData: {},
        clientData: clientData as unknown as Record<string, unknown>,
        formData,
        apiPayloadExtras: extras,
        formId,
        tracking,
        templateName,
        submissionMetadata: getSubmissionMetadata(formData),
      });

      const result = await submitAction(payload);

      if (result.success && formData.hasSecondPage !== true) {
        resetSubmissionMetadata();
      }

      if (!result.success) {
        setSubmissionErrorMessage(SUBMISSION_ERROR_MESSAGE);
        sendEvent({
          name: 'email_capture',
          action: 'form_error',
          category: 'Form Interaction',
          label: result.error || 'Submission failed',
        });
      }

      return result;
    } catch (err) {
      setSubmissionErrorMessage(SUBMISSION_ERROR_MESSAGE);
      sendEvent({
        name: 'email_capture',
        action: 'form_error',
        category: 'Form Interaction',
        label: err instanceof Error ? err.message : 'Unknown error',
      });
      return { success: false, error: 'An unexpected error occurred' };
    } finally {
      setIsLoading(false);
    }
  };

  return {
    submitToApi,
    resetSubmissionMetadata,
    submissionErrorMessage,
    isLoading,
  };
}
